import { InputWrapper, Label } from 'components/Form/Input'
import Select from 'components/Form/Select'
import I18n from 'components/I18n'
import { OverlayLoader } from 'components/Loaders'
import { trackEvent } from 'data/Analytics/actions'
import appActions from 'data/App/actions'
import appSelectors from 'data/App/selectors'
import { UserType } from 'data/types'
import messages from 'lang'
import { validate as postcodeValidate } from 'postal-codes-js'
import React, { useEffect, useState } from 'react'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

import { Button, Title, Wrapper } from '../styles'
import CountrySpecificFields from './CountrySpecificFields'

type Address = {
  city: string
  country: string
  firstLine: string
  postcode: string
  secondLine?: string
  state?: string
}

type Props = {
  address: Address
  loading: boolean
  onSubmit: (address: Address) => void
  user: UserType
} & WrappedComponentProps

const Address = ({ address, intl, loading, onSubmit, user }: Props) => {
  const dispatch = useDispatch()
  const countries = useSelector(appSelectors.getExchangeAllowedCountries)
  const usStates = useSelector(appSelectors.getRawUsStates)
  useEffect(function fetchCountriesOnMount() {
    dispatch(appActions.getCountries())
  }, [])
  const [country, setCountry] = useState(address.country)
  const [state, setState] = useState(address.state ?? '') // general country state
  const [usState, setUsState] = useState(address.state ?? 'US-AL') // us specific, from usStates list
  const [addressLine1, setAddressLine1] = useState(address.firstLine)
  const [addressLine2, setAddressLine2] = useState(address.secondLine ?? '')
  const [postalCode, setPostalCode] = useState(address.postcode)
  const [city, setCity] = useState(address.city)
  const isPostalCodeValid = postcodeValidate(country, postalCode) === true

  if (!countries.length || !usStates.length || loading)
    return (
      <Wrapper>
        <OverlayLoader />
      </Wrapper>
    )

  // Only states may not be supported. Countries are all ok
  const isSupportedUSState =
    usStates.find((s) => s.code === usState)?.scopes.includes('MERCURY') ?? true
  const isSupported = country !== 'US' || isSupportedUSState
  const notifyUnsuportedSignup = () =>
    dispatch(
      trackEvent({
        key: 'Sign Up Address Unsupported',
        properties: {
          country,
          country_state: country === 'US' ? usState : state,
          email: user.email
        }
      })
    )

  return (
    <Wrapper>
      <header>
        <Title>
          <I18n data={messages.address} />
        </Title>
      </header>
      <main>
        <InputWrapper>
          <Label htmlFor='kyc-country'>
            <I18n data={messages.label_country} />
          </Label>
          <Select name='kyc-country' onChange={(e) => setCountry(e.target.value)} value={country}>
            {countries
              .sort((c1, c2) => (c1.name > c2.name ? 1 : -1))
              .map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
          </Select>
        </InputWrapper>
        <CountrySpecificFields
          addressLine1={addressLine1}
          addressLine2={addressLine2}
          city={city}
          country={country}
          intl={intl}
          isPostalCodeValid={isPostalCodeValid}
          isSupported={isSupported}
          postalCode={postalCode}
          setAddressLine1={setAddressLine1}
          setAddressLine2={setAddressLine2}
          setCity={setCity}
          setPostalCode={setPostalCode}
          setState={setState}
          setUsState={setUsState}
          state={state}
          usState={usState}
          usStates={usStates}
        />
      </main>
      <footer>
        {isSupported ? (
          <Button
            disabled={city === '' || addressLine1 === '' || !isPostalCodeValid}
            onClick={() =>
              onSubmit({
                city,
                country,
                firstLine: addressLine1,
                postcode: postalCode,
                secondLine: addressLine2 !== '' ? addressLine2 : undefined,
                state: country === 'US' ? usState : country === 'GB' ? state : undefined
              })
            }
          >
            <I18n data={messages.button_continue} />
          </Button>
        ) : (
          <>
            <p>
              <I18n data={messages.signup_unavailable} />
            </p>
            <Button type='button' onClick={notifyUnsuportedSignup}>
              <I18n data={messages.notify_email} />
            </Button>
          </>
        )}
      </footer>
    </Wrapper>
  )
}

export default injectIntl(Address)
